import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { API_BASE_URL, API_ENDPOINTS } from '../config';
import {
  RANGES, MONTHS, rangeStart, strategyGrowth, curveStats, monthlyReturns,
} from '../utils/publicShareMath';
import ShareTradeList from '../components/publicShare/ShareTradeList';
import { initialShareMode, shareLayout, shareLookVars } from '../utils/shareLook';
import './PublicSharePage.css';

/**
 * Public results page (/s/<token>): a brand's chosen strategies for prospects,
 * no account needed. A lighter Overview: one or more strategies, each its own
 * line and color, against any of the market comparisons the link offers, for a
 * period and a starting capital, plus monthly returns. Rendered outside the
 * app (no sign-in, no sidebar). With `previewView` it renders a draft link
 * inside the link editor instead of loading one.
 */

const VIEW_LABEL = { live: 'Live', backtested: 'Backtested', full: 'Full Journey' };
const CAPITALS = [25000, 50000, 100000, 250000];
// Links made before comparisons were configurable offered these two.
const DEFAULT_BENCHMARKS = [
  { id: 'SPY', label: 'S&P 500', color: '#f97316' },
  { id: 'QQQ', label: 'Nasdaq-100', color: '#ef4444' },
];
const PALETTE = ['#3b82f6', '#a855f7', '#22c55e', '#06b6d4', '#ec4899', '#84cc16'];

// What RiskLo does, for the "Powered by RiskLo" section (marketing/catalog.js has the full list).
const PROMO_FEATURES = [
  'All your brokers in one place: NinjaTrader, Interactive Brokers, Schwab, Robinhood, Coinbase and more',
  'Portfolio risk for stocks, ETFs and crypto: position sizing, concentration, drawdown and VaR',
  'Prop-firm and futures risk: blowout odds and consistency rules',
  'Portfolio Simulator to combine strategies and accounts',
  'Custom dashboards, and RiskLo AI to ask about your own trades',
];

const tokenFromPath = () => (window.location.pathname.match(/^\/s\/([A-Za-z0-9_-]+)/) || [])[1] || '';
const emailKey = (token) => `riskloShare:${token}`;
const readEmail = (token) => { try { return localStorage.getItem(emailKey(token)) || ''; } catch (_e) { return ''; } };
const saveEmail = (token, email) => { try { localStorage.setItem(emailKey(token), email); } catch (_e) { /* private mode */ } };

const usd = (n, { sign = false, compact = false } = {}) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  const body = compact && abs >= 1000
    ? `$${(abs / 1000).toLocaleString('en-US', { maximumFractionDigits: abs >= 100000 ? 0 : 1 })}k`
    : `$${Math.round(abs).toLocaleString('en-US')}`;
  if (v < 0) return `−${body}`;
  return sign && v > 0 ? `+${body}` : body;
};
const pct = (n, { sign = true, digits = 1 } = {}) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n) * 100;
  const s = `${Math.abs(v).toFixed(digits)}%`;
  if (v < 0) return `−${s}`;
  return sign && v > 0 ? `+${s}` : s;
};
const longDate = (iso) => (iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—');
const axisDate = (long) => (iso) => {
  const d = new Date(`${iso}T12:00:00Z`);
  if (!long) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} '${String(d.getUTCFullYear()).slice(2)}`;
};
const sizeText = (c) => {
  if (c === 1) return '1 NQ contract';
  if (c < 1) {
    const micros = Math.round(c * 10);
    return `${micros} MNQ (micro) contract${micros === 1 ? '' : 's'}`;
  }
  return `${c} NQ contracts`;
};

/**
 * Toggle buttons with a color swatch: any number on. With minOne the last one
 * on can't be turned off (a strategy always shows); without it, all can be off.
 */
function ColorPills({ options, values, onChange, label, minOne = false }) {
  const toggle = (v) => {
    if (values.includes(v)) {
      if (!minOne || values.length > 1) onChange(values.filter((x) => x !== v));
    } else {
      onChange([...values, v]);
    }
  };
  return (
    <div className="psp-pills" role="group" aria-label={label}>
      {options.map((o) => {
        const on = values.includes(o.value);
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={on}
            className={`psp-color-pill${on ? ' is-on' : ''}`}
            style={on ? { borderColor: o.color, boxShadow: `inset 0 0 0 1px ${o.color}` } : undefined}
            onClick={() => toggle(o.value)}
          >
            <i className="psp-swatch" style={{ background: on ? o.color : 'transparent', borderColor: o.color }} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Pills({ options, value, onChange, label }) {
  return (
    <div className="psp-pills" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" role="tab" aria-selected={value === o.value} className={value === o.value ? 'is-active' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ChartTip({ active, payload, label, capital, lines }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="psp-tip">
      <span>{longDate(label || row.date)}</span>
      {lines.map((l) => (row[l.key] != null ? (
        <div key={l.key}><i className="psp-key" style={{ background: l.color }} />{l.label}: <strong>{usd(row[l.key])}</strong> ({pct(row[l.key] / capital - 1)})</div>
      ) : null))}
    </div>
  );
}

/** Value of `values` ([{date, value}], sorted) on `date`: the last one on or before it. */
function valueOn(values, date, state) {
  while (state.i < values.length && values[state.i].date <= date) { state.last = values[state.i].value; state.i += 1; }
  return state.last;
}

function Gate({ view, token, onPassed }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.publicShareLead(token), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.error || 'Couldn’t continue. Try again.');
      saveEmail(token, email.trim().toLowerCase());
      onPassed(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="psp-gate" onSubmit={submit}>
      <h2>{view.title}</h2>
      <p>Enter your email to see {view.brand.name}’s results.</p>
      <div className="psp-gate-row">
        <input type="email" required value={email} placeholder="you@example.com" onChange={(e) => setEmail(e.target.value)} aria-label="Email" />
        <button type="submit" disabled={busy}>{busy ? 'One moment…' : 'See results'}</button>
      </div>
      {error ? <p className="psp-error">{error}</p> : null}
    </form>
  );
}

/** Monthly returns: one row per year, a cell per month, as % of the starting capital. */
function MonthlyGrid({ months }) {
  const years = Object.keys(months.byYear).sort().reverse();
  if (!years.length) return null;
  const tint = (v) => {
    const a = Math.min(0.55, Math.abs(v) * 6 + 0.08);
    return v >= 0 ? `rgba(34, 160, 90, ${a})` : `rgba(220, 70, 70, ${a})`;
  };
  return (
    <div className="psp-months-wrap">
      <table className="psp-months">
        <thead><tr><th>Year</th>{MONTHS.map((m) => <th key={m}>{m}</th>)}<th>Year</th></tr></thead>
        <tbody>
          {years.map((y) => {
            const row = months.byYear[y];
            return (
              <tr key={y}>
                <th scope="row">{y}</th>
                {MONTHS.map((m, i) => {
                  const v = row.months[i];
                  return <td key={m} style={v != null ? { background: tint(v) } : undefined} title={v != null ? `${m} ${y}: ${pct(v)}` : undefined}>{v != null ? pct(v) : ''}</td>;
                })}
                <td className="psp-months-total">{pct(row.total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function PublicSharePage({ previewView = null }) {
  const isPreview = Boolean(previewView);
  const token = isPreview ? '' : tokenFromPath();
  const [view, setView] = useState(previewView);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(null);
  const [picked, setPicked] = useState([]); // strategy names shown
  const [benchIds, setBenchIds] = useState([]); // market comparisons shown
  const [range, setRange] = useState('All');
  const [capital, setCapital] = useState(100000);
  const [modeChoice, setModeChoice] = useState(null); // 'dark' | 'light' once the viewer switches
  const [monthlyFor, setMonthlyFor] = useState(null);
  const [benchSeries, setBenchSeries] = useState({}); // `${id}|${start}|${capital}` → [{ date, normalized }]

  const load = useCallback(async () => {
    if (isPreview) return;
    try {
      const email = readEmail(token);
      const res = await fetch(API_ENDPOINTS.publicShare(token), { headers: email ? { 'x-share-email': email } : {} });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.error || 'Couldn’t load these results.');
      setView(body);
    } catch (err) {
      setError(err.message);
    }
  }, [token, isPreview]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (previewView) setView(previewView); }, [previewView]);

  const strategies = useMemo(
    () => (view?.strategies || []).map((s, i) => ({ ...s, color: s.color || PALETTE[i % PALETTE.length] })),
    [view],
  );
  const benchmarks = useMemo(() => (view?.benchmarks || DEFAULT_BENCHMARKS), [view]);

  // A new page (or a changed preview) starts from the link's own defaults:
  // the strategies marked to show first (else the first one), its comparisons, capital and period.
  useEffect(() => {
    if (!view || view.gate) return;
    if (!isPreview && !view.title) return;
    if (view.title && !isPreview) document.title = `${view.title} · ${view.brand?.name || 'RiskLo'}`;
    setTab(view.defaultView);
    const initial = strategies.filter((s) => s.initial).map((s) => s.name);
    setPicked(initial.length ? initial : strategies.slice(0, 1).map((s) => s.name));
    setBenchIds(Array.isArray(view.initialBenchmarks) ? view.initialBenchmarks : ['SPY']);
    setCapital(view.defaultCapital || 100000);
    setRange(view.defaultRange || 'All');
    setMonthlyFor(null);
  }, [view, strategies, isPreview]);
  useEffect(() => {
    if (view?.gate && view?.title && !isPreview) document.title = `${view.title} · ${view.brand?.name || 'RiskLo'}`;
  }, [view, isPreview]);

  const pickedNames = useMemo(() => {
    const kept = picked.filter((n) => strategies.some((s) => s.name === n));
    return kept.length ? kept : strategies.slice(0, 1).map((s) => s.name);
  }, [picked, strategies]);
  const chosen = useMemo(() => strategies.filter((s) => pickedNames.includes(s.name)), [strategies, pickedNames]);
  const shownBench = useMemo(() => benchmarks.filter((b) => benchIds.includes(b.id)), [benchmarks, benchIds]);
  const start = rangeStart(range);

  // Each chosen strategy's growth of the capital, windowed to the view and period.
  const strategyLines = useMemo(() => {
    if (!tab) return [];
    return chosen.map((s, i) => ({
      key: `s${i}`, label: s.name, color: s.color, kind: 'strategy',
      values: strategyGrowth(s, tab, start, capital),
    })).filter((l) => l.values.length >= 2);
  }, [chosen, tab, start, capital]);
  const dates = useMemo(
    () => [...new Set(strategyLines.flatMap((l) => l.values.map((p) => p.date)))].sort(),
    [strategyLines],
  );
  const firstDate = dates[0] || null;

  // Market comparisons: the same capital from the same first day (public, cached on the server).
  // One request per comparison, start day and capital; each answer is kept.
  const requested = useRef(new Set());
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    if (!firstDate) return;
    shownBench.forEach((b) => {
      const key = `${b.id}|${firstDate}|${capital}`;
      if (requested.current.has(key)) return;
      requested.current.add(key);
      const keep = (series) => { if (mounted.current) setBenchSeries((m) => ({ ...m, [key]: series })); };
      fetch(`${API_BASE_URL}/api/public/embed/benchmark-series?benchmark=${b.id}&startDate=${firstDate}&baseValue=${capital}`)
        .then((r) => r.json())
        .then((body) => keep(body?.series || []))
        .catch(() => keep([]));
    });
  }, [shownBench, firstDate, capital]);

  // Each comparison on the strategies' dates (last close carried forward), so all lines share one axis.
  const benchLines = useMemo(() => shownBench.map((b) => {
    const series = (benchSeries[`${b.id}|${firstDate}|${capital}`] || [])
      .map((p) => ({ date: p.date, value: p.normalized }))
      .sort((x, y) => (x.date < y.date ? -1 : 1));
    const state = { i: 0, last: null };
    const values = dates
      .map((date) => ({ date, value: valueOn(series, date, state) }))
      .filter((p) => p.value != null);
    if (values.length && values[0].date !== dates[0]) values.unshift({ date: dates[0], value: capital });
    return { key: `b_${b.id}`, label: b.label, color: b.color, kind: 'bench', id: b.id, values };
  }), [shownBench, benchSeries, dates, capital, firstDate]);

  const lines = useMemo(() => [...strategyLines, ...benchLines.filter((l) => l.values.length >= 2)], [strategyLines, benchLines]);
  // One row per date; a line's value carries over days it has none, inside its own span.
  const chart = useMemo(() => {
    const states = lines.map(() => ({ i: 0, last: null }));
    return dates.map((date) => {
      const row = { date };
      lines.forEach((l, i) => {
        const endDate = l.values[l.values.length - 1]?.date;
        const v = valueOn(l.values, date, states[i]);
        row[l.key] = date > endDate ? null : v;
      });
      return row;
    });
  }, [lines, dates]);
  const statsByKey = useMemo(() => {
    const out = {};
    lines.forEach((l) => { out[l.key] = curveStats(l.values, capital); });
    return out;
  }, [lines, capital]);

  const primary = strategyLines[0] || null;
  const stats = primary ? statsByKey[primary.key] : null;
  const firstBench = benchLines.find((l) => l.values.length >= 2) || null;
  const benchStats = firstBench ? statsByKey[firstBench.key] : null;
  const vsBench = stats && benchStats ? stats.returnPct - benchStats.returnPct : null;
  const monthlyLine = strategyLines.find((l) => l.label === monthlyFor) || primary;
  const months = useMemo(() => monthlyReturns(monthlyLine?.values || []), [monthlyLine]);

  // The brand's look (Brand settings → Colors & layout) when the link uses it:
  // colors for the current mode, its fonts and layout style.
  const brandLook = view?.brand || {};
  const mode = modeChoice || initialShareMode(brandLook.layout);
  const layoutLook = useMemo(() => shareLayout(brandLook.layout), [brandLook.layout]);
  const style = useMemo(
    () => ({ ...shareLookVars(brandLook.theme, mode, brandLook.accentColor), ...layoutLook.vars }),
    [brandLook.theme, brandLook.accentColor, mode, layoutLook],
  );
  const rootClass = `psp${layoutLook.className}${isPreview ? ' is-preview' : ''}`;
  const modeButton = (
    <button
      type="button"
      className="psp-mode"
      onClick={() => setModeChoice(mode === 'dark' ? 'light' : 'dark')}
      aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      title={mode === 'dark' ? 'Light mode' : 'Dark mode'}
    >
      {mode === 'dark' ? '☀' : '☾'}
    </button>
  );
  const brandHead = view ? (
    <header className="psp-top">
      <div className="psp-brand">
        {view.brand.logoUrl ? <img src={`${API_BASE_URL}${view.brand.logoUrl}`} alt="" /> : null}
        <span>{view.brand.name}</span>
      </div>
      <div className="psp-top-actions">
        {modeButton}
        {view.brand.checkoutUrl && !view.gate ? <a className="psp-cta" href={view.brand.checkoutUrl} target="_blank" rel="noreferrer">Join {view.brand.name}</a> : null}
      </div>
    </header>
  ) : null;

  if (error) {
    return <main className={rootClass} data-mode={mode}><div className="psp-inner"><div className="psp-card psp-empty"><h2>Not available</h2><p>{error}</p></div></div></main>;
  }
  if (!view) return <main className={rootClass} data-mode={mode}><div className="psp-inner"><p className="psp-muted">Loading…</p></div></main>;
  if (view.gate) {
    return (
      <main className={rootClass} style={style} data-mode={mode}>
        <div className="psp-inner">
          {brandHead}
          <div className="psp-card"><Gate view={view} token={token} onPassed={(v) => { setView(v); setTab(null); }} /></div>
        </div>
      </main>
    );
  }

  // "Live since" only when every chosen strategy has a live date; otherwise the numbers include full histories.
  const liveDates = chosen.every((s) => s.liveSince) ? chosen.map((s) => s.liveSince).sort() : [];
  const lastDay = dates[dates.length - 1] || null;
  const firstDay = dates[1] || dates[0] || null;
  const longSpan = firstDay && lastDay ? (Date.parse(lastDay) - Date.parse(firstDay)) / 86400000 > 400 : false;
  const strategyTitle = strategyLines.map((l) => l.label).join(', ');
  const benchTitle = benchLines.map((l) => l.label).join(', ');
  const sections = view.sections || { monthly: true, included: true, promo: true };

  return (
    <main className={rootClass} style={style} data-mode={mode}>
      <div className="psp-inner">
        {brandHead}

        <section className="psp-hero">
          <h1>{view.title}</h1>
          <p className="psp-muted">
            {tab === 'live' && liveDates.length ? `Live results since ${longDate(liveDates[0])}` : `${tab === 'backtested' ? 'Backtested results' : tab === 'full' && view.views.length > 1 ? 'Full Journey' : 'Results'}${firstDay ? ` from ${longDate(firstDay)}` : ''}`}
            {lastDay ? ` · through ${longDate(lastDay)}` : ''}
          </p>
          {view.intro ? <p className="psp-intro">{view.intro}</p> : null}
        </section>

        <section className="psp-card psp-controls">
          {/* Shown even for one strategy, so prospects see which it is (it can't be turned off). */}
          {strategies.length ? (
            <div className="psp-control">
              <span>{strategies.length > 1 ? 'Strategies' : 'Strategy'}</span>
              <ColorPills
                label="Strategies"
                values={pickedNames}
                minOne
                onChange={setPicked}
                options={strategies.map((s) => ({ value: s.name, label: s.name, color: s.color }))}
              />
            </div>
          ) : null}
          {view.views.length > 1 ? (
            <div className="psp-control">
              <span>Results</span>
              <Pills label="Results" value={tab} onChange={setTab} options={view.views.map((v) => ({ value: v, label: VIEW_LABEL[v] }))} />
            </div>
          ) : null}
          <div className="psp-control-row">
            <div className="psp-control">
              <span>Starting capital</span>
              <Pills label="Starting capital" value={capital} onChange={setCapital} options={CAPITALS.map((c) => ({ value: c, label: usd(c, { compact: true }) }))} />
            </div>
            <div className="psp-control">
              <span>Period</span>
              <Pills label="Period" value={range} onChange={setRange} options={RANGES.map((r) => ({ value: r, label: r }))} />
            </div>
          </div>
          {benchmarks.length ? (
            <div className="psp-control">
              <span>Compare with markets</span>
              <ColorPills
                label="Compare with markets"
                values={benchIds}
                onChange={setBenchIds}
                options={benchmarks.map((b) => ({ value: b.id, label: b.label, color: b.color }))}
              />
            </div>
          ) : null}
        </section>

        {stats && primary ? (
          <>
            <section className="psp-stats" aria-label={`${primary.label} headline numbers`}>
              <div className="psp-stat psp-stat--hero">
                <span><i className="psp-key" style={{ background: primary.color }} />{strategyLines.length > 1 ? primary.label : 'Return'}</span>
                <b className={stats.returnPct < 0 ? 'is-neg' : 'is-pos'}>{pct(stats.returnPct)}</b>
                <small>{usd(stats.net, { sign: true })} on {usd(capital)}</small>
              </div>
              <div className="psp-stat">
                <span>vs {firstBench?.label || 'markets'}</span>
                <b className={vsBench == null ? '' : vsBench < 0 ? 'is-neg' : 'is-pos'}>{vsBench != null ? pct(vsBench) : '—'}</b>
                <small>{benchStats ? `${firstBench.label} ${pct(benchStats.returnPct)} same period` : 'Pick a market to compare'}</small>
              </div>
              <div className="psp-stat">
                <span>Max drawdown</span>
                <b>{pct(stats.maxDrawdownPct, { sign: false })}</b>
                <small>{usd(stats.maxDrawdown)}</small>
              </div>
              <div className="psp-stat">
                <span>Winning days</span>
                <b>{pct(stats.winRate, { sign: false })}</b>
                <small>{stats.tradingDays.toLocaleString('en-US')} days with a result</small>
              </div>
              <div className="psp-stat">
                <span>Profit factor</span>
                <b>{stats.profitFactor != null ? stats.profitFactor.toFixed(2) : '—'}</b>
                <small>gains ÷ losses</small>
              </div>
              <div className="psp-stat">
                <span>Positive months</span>
                <b>{pct(stats.positiveMonths, { sign: false, digits: 0 })}</b>
                <small>best {pct(stats.bestMonth)} · worst {pct(stats.worstMonth)}</small>
              </div>
            </section>

            <section className="psp-card">
              <div className="psp-chart-head">
                <div>
                  <span className="psp-eyebrow">Growth of {usd(capital)}</span>
                  <h2>{strategyTitle}{benchTitle ? ` vs ${benchTitle}` : ''}</h2>
                </div>
              </div>
              <div className="psp-chart" role="img" aria-label={`Growth of ${usd(capital)}: ${lines.map((l) => l.label).join(', ')}`}>
                <ResponsiveContainer width="100%" height={360}>
                  <LineChart data={chart} margin={{ top: 10, right: 8, bottom: 0, left: 4 }}>
                    <CartesianGrid vertical={false} stroke="var(--psp-grid)" />
                    <XAxis dataKey="date" tickFormatter={axisDate(longSpan)} minTickGap={48} tick={{ fill: 'var(--psp-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis domain={['auto', 'auto']} tickFormatter={(v) => usd(v, { compact: true })} width={64} tick={{ fill: 'var(--psp-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <ReferenceLine y={capital} stroke="var(--psp-grid-strong)" strokeDasharray="4 4" />
                    <Tooltip content={<ChartTip capital={capital} lines={lines} />} cursor={{ stroke: 'var(--psp-muted)', strokeDasharray: '3 3' }} />
                    {lines.map((l) => (
                      <Line
                        key={l.key}
                        type="monotone"
                        dataKey={l.key}
                        name={l.label}
                        stroke={l.color}
                        strokeWidth={l.kind === 'bench' ? 1.6 : 2.2}
                        strokeDasharray={l.kind === 'bench' ? '5 3' : undefined}
                        dot={false}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="psp-legend">
                {lines.map((l) => (
                  <span key={l.key}>
                    <i className={`psp-key${l.kind === 'bench' ? ' is-dashed' : ''}`} style={{ background: l.color, color: l.color }} />
                    {l.label} <b>{pct(statsByKey[l.key]?.returnPct)}</b>
                  </span>
                ))}
              </div>
              <p className="psp-muted psp-small">
                {view.units} Returns are on a fixed starting capital.{benchLines.length ? ' Markets use their ETF or futures price as a proxy (dashed lines).' : ''}
              </p>
            </section>

            {lines.length > 1 ? (
              <section className="psp-card">
                <span className="psp-eyebrow">Side by side</span>
                <h2>Compare</h2>
                <div className="psp-compare-wrap">
                  <table className="psp-compare">
                    <thead>
                      <tr><th scope="col">Line</th><th scope="col">Return</th><th scope="col">Gain</th><th scope="col">Max drawdown</th><th scope="col">Winning days</th><th scope="col">Profit factor</th><th scope="col">Positive months</th></tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => {
                        const st = statsByKey[l.key];
                        return (
                          <tr key={l.key}>
                            <th scope="row"><i className={`psp-key${l.kind === 'bench' ? ' is-dashed' : ''}`} style={{ background: l.color, color: l.color }} />{l.label}</th>
                            <td className={st.returnPct < 0 ? 'is-neg' : 'is-pos'}>{pct(st.returnPct)}</td>
                            <td>{usd(st.net, { sign: true })}</td>
                            <td>{pct(st.maxDrawdownPct, { sign: false })}</td>
                            <td>{pct(st.winRate, { sign: false })}</td>
                            <td>{st.profitFactor != null ? st.profitFactor.toFixed(2) : '—'}</td>
                            <td>{pct(st.positiveMonths, { sign: false, digits: 0 })}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            {sections.monthly ? (
              <section className="psp-card">
                <span className="psp-eyebrow">Monthly returns</span>
                <h2>Month by month{strategyLines.length > 1 ? '' : `: ${primary.label}`}</h2>
                {strategyLines.length > 1 ? (
                  <div className="psp-months-pick">
                    <Pills label="Monthly returns for" value={monthlyLine?.label} onChange={setMonthlyFor} options={strategyLines.map((l) => ({ value: l.label, label: l.label }))} />
                  </div>
                ) : null}
                <MonthlyGrid months={months} />
                <p className="psp-muted psp-small">Each month’s change in the growth curve above.</p>
              </section>
            ) : null}
          </>
        ) : <section className="psp-card"><p className="psp-muted">No results in this period. Try a longer one.</p></section>}

        {sections.included && strategies.length ? (
          <section className="psp-card psp-strats">
            <span className="psp-eyebrow">What’s included</span>
            <ul>
              {strategies.map((s) => (
                <li key={s.name}>
                  <strong><i className="psp-key" style={{ background: s.color }} />{s.name}</strong>
                  <span>{s.model === 'compounded' ? 'Equity curve' : sizeText(s.contracts)}</span>
                  <span>{s.liveSince ? `Live since ${longDate(s.liveSince)}` : 'Full history'}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {sections.trades ? <ShareTradeList strategies={chosen} view={tab} capital={capital} /> : null}

        {sections.promo ? (
          <section className="psp-promo">
            <div>
              <span className="psp-eyebrow">Powered by RiskLo</span>
              <h2>One place for all your trading and investing</h2>
              <p>
                {view.brand.name} tracks its results on RiskLo. Bring every brokerage account together, read-only, and
                see the risk across futures, stocks, ETFs and crypto before you take the trade.
              </p>
              <ul className="psp-promo-list">
                {PROMO_FEATURES.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </div>
            <div className="psp-promo-actions">
              {view.brand.checkoutUrl ? <a className="psp-cta" href={view.brand.checkoutUrl} target="_blank" rel="noreferrer">Join {view.brand.name}</a> : null}
              <a className="psp-cta psp-cta--ghost" href="/features" target="_blank" rel="noreferrer">Explore RiskLo</a>
            </div>
          </section>
        ) : null}

        <footer className="psp-disclaimer">
          {view.disclaimers?.backtested && tab !== 'live' ? <p>{view.disclaimers.backtested}</p> : null}
          {view.disclaimers?.unsplit ? <p>{view.disclaimers.unsplit}</p> : null}
          <p>{view.disclaimers?.general}</p>
          {/* Without the RiskLo section (the brand turned it off), no link to RiskLo either. */}
          <p className="psp-powered">Powered by {sections.promo ? <a href="/" target="_blank" rel="noreferrer">RiskLo</a> : 'RiskLo'}</p>
        </footer>
      </div>
    </main>
  );
}
